import { useEffect, useRef } from 'react';

const DRAG_THRESHOLD = 5;

const useHorizontalDrag = ({
  onMoveStart,
  onMove,
  threshold = DRAG_THRESHOLD,
  itemsOnLeftClassName = null,
  itemsOnRightClassName = null,
  margin = 0.02
}) => {
  const startDragPosition = useRef(null);
  const scrollLeftStart = useRef(null);
  const hasDragged = useRef(false);
  const containerRef = useRef(null);

  const checkItemsOnLeftHidden = () => {
    if (!itemsOnLeftClassName) return;

    const firstItemWidth = containerRef.current.children[0]?.clientWidth;
    if (containerRef.current.scrollLeft >= firstItemWidth * margin) {
      containerRef.current.classList.add(itemsOnLeftClassName);
    } else {
      containerRef.current.classList.remove(itemsOnLeftClassName);
    }
  };

  const checkItemsOnRightHidden = () => {
    if (!itemsOnRightClassName) return;

    if (
      containerRef.current.scrollLeft + containerRef.current.clientWidth <=
      containerRef.current.scrollWidth * (1 - margin)
    ) {
      containerRef.current.classList.add(itemsOnRightClassName);
    } else {
      containerRef.current.classList.remove(itemsOnRightClassName);
    }
  };

  const handleMove = currentPosition => {
    const delta = currentPosition - startDragPosition.current;
    if (Math.abs(delta) > threshold) {
      if (!hasDragged.current) {
        hasDragged.current = true;
        onMoveStart();
      }
    }
    containerRef.current.scrollLeft = scrollLeftStart.current - delta;
    const nChildren = containerRef.current.childNodes.length;
    const childWidth = containerRef.current.scrollWidth / nChildren;
    const containerCenter =
      containerRef.current.scrollLeft + containerRef.current.clientWidth / 2;
    const selectedIteminMiddle = Math.floor(containerCenter / childWidth);

    checkItemsOnLeftHidden();
    checkItemsOnRightHidden();
    onMove(selectedIteminMiddle);
  };

  // Implement touch events
  const handleTouchMove = e => {
    const currentPosition = e.touches[0].pageX;
    handleMove(currentPosition);
  };

  const handleMouseMove = e => {
    const currentPosition = e.pageX;
    handleMove(currentPosition);
  };

  const handleTouchStart = e => {
    startDragPosition.current = e.touches[0].pageX;
    scrollLeftStart.current = containerRef.current.scrollLeft;
    hasDragged.current = false;
    containerRef.current.style.cursor = 'grabbing';

    // eslint-disable-next-line no-use-before-define
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', handleTouchMove);
    handleTouchMove(e);
  };

  const handleTouchEnd = () => {
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }

    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchmove', handleTouchMove);

    // Reset the drag flag after a short delay to allow click prevention
    setTimeout(() => {
      hasDragged.current = false;
    }, 100);
  };

  const handleMouseDown = e => {
    startDragPosition.current = e.pageX;
    scrollLeftStart.current = containerRef.current.scrollLeft;
    hasDragged.current = false;
    containerRef.current.style.cursor = 'grabbing';

    // eslint-disable-next-line no-use-before-define
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    handleMouseMove(e);
  };

  const handleMouseUp = () => {
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }

    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('mousemove', handleMouseMove);

    // Reset the drag flag after a short delay to allow click prevention
    setTimeout(() => {
      hasDragged.current = false;
    }, 100);
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
    checkItemsOnLeftHidden();
    checkItemsOnRightHidden();
  }, []);

  const navigateToItem = index => {
    const nChildren = containerRef.current.childNodes.length;
    const childWidth = containerRef.current.scrollWidth / nChildren;
    const scrollPosition = index * childWidth;

    containerRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  return {
    isDragging: () => hasDragged.current,
    containerRef,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    navigateToItem
  };
};

export default useHorizontalDrag;
